import { useState, useEffect } from 'react';
import { useSignMessage, useAccount } from 'wagmi';
import { SuccessPopup } from './SuccessPopup';

const imgUsdcStreamlineCryptocurrency = '/assets/earn/usdc.svg';
const imgMultiCollateralDaiDaiLogo1 = '/assets/earn/dai.svg';
const imgUsdt = '/assets/earn/usdt.png';
const imgBnb = '/assets/earn/bnb.png';

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || 'https://api.zkpoolprotocol.xyz';

const tokensConfig = [
    {
        label: 'USDC-Test',
        tokenName: 'USDC',
        icon: imgUsdcStreamlineCryptocurrency,
        accent: '#39e5f5', // Accent color when active
    },
    {
        label: 'DAI-Test',
        tokenName: 'DAI',
        icon: imgMultiCollateralDaiDaiLogo1,
        accent: '#39e5f5', // Accent color when active
    },
    {
        label: 'USDT-Test',
        tokenName: 'USDT',
        icon: imgUsdt,
        accent: '#39e5f5', // Accent color when active
    },
    {
        label: 'BNB-Test',
        tokenName: 'BNB',
        icon: imgBnb,
        accent: '#39e5f5', // Accent color when active
    },
];

// Style constants
const STYLES = {
    // Default state (not selected and not staked)
    normal: {
        border: '#2b2b2b',
        backgroundColor: 'rgba(255,255,255,0.02)',
        text: '#c8c8c8',
    },
    // Active state (selected)
    active: {
        // Use the token accent color
    },
    // Disabled state (already staked)
    disabled: {
        border: '#4b4b4b',
        backgroundColor: '#4d4d4d',
        text: '#7d7d7d',
    },
};

interface StakedToken {
    wallet_address: string;
    token_name: string;
    amount: number;
    staked_at: string;
}

export function StakeLiquidityPrivately() {
    const [selectedToken, setSelectedToken] = useState<string | null>(null);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [stakedTokens, setStakedTokens] = useState<Set<string>>(new Set());
    const [isLoadingStakes, setIsLoadingStakes] = useState(false);
    const { signMessage } = useSignMessage();
    const { address, isConnected } = useAccount();

    // Fetch staked token list
    useEffect(() => {
        if (!isConnected || !address) {
            setStakedTokens(new Set<string>());
            setSelectedToken(null);
            return;
        }

        const fetchStakedTokens = async () => {
            setIsLoadingStakes(true);
            try {
                const response = await fetch(`${API_BASE_URL}/stake/${address}`);
                const result = await response.json();
                
                if (result.code === 0 && Array.isArray(result.data)) {
                    const stakedTokenNames = new Set<string>(
                        result.data.map((item: StakedToken) => item.token_name.toUpperCase())
                    );
                    setStakedTokens(stakedTokenNames);
                } else {
                    setStakedTokens(new Set<string>());
                }
            } catch (error) {
                console.error('Failed to fetch staked tokens:', error);
                setStakedTokens(new Set<string>());
            } finally {
                setIsLoadingStakes(false);
            }
        };

        fetchStakedTokens();
    }, [isConnected, address]);

    // Clear selection if the currently selected token is already staked
    useEffect(() => {
        if (selectedToken && stakedTokens.size > 0) {
            const selectedTokenConfig = tokensConfig.find(t => t.label === selectedToken);
            if (selectedTokenConfig && stakedTokens.has(selectedTokenConfig.tokenName.toUpperCase())) {
                setSelectedToken(null);
            }
        }
    }, [stakedTokens, selectedToken]);

    // Update tokens array based on staked tokens
    const tokens = tokensConfig.map(token => ({
        ...token,
        disabled: stakedTokens.has(token.tokenName.toUpperCase()),
    }));

    const handleStake = () => {
        if (!selectedToken || !address) return;

        const selectedTokenConfig = tokensConfig.find(t => t.label === selectedToken);
        if (!selectedTokenConfig) return;

        const message = `Staking 100 ${selectedToken} privately`;
        signMessage({ message }, {
            onSuccess: async (data) => {
                console.log('Signature:', data);
                
                // Call POST /stake to record the stake
                try {
                    const response = await fetch(`${API_BASE_URL}/stake`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            wallet_address: address,
                            token: selectedTokenConfig.tokenName,
                        }),
                    });

                    const result = await response.json();
                    
                    if (result.code === 0) {
                        // Update staked token list
                        setStakedTokens(prev => new Set([...prev, selectedTokenConfig.tokenName.toUpperCase()]));
                        setSelectedToken(null);
                        setShowSuccessPopup(true);
                    } else {
                        console.error('Stake API error:', result.msg);
                        alert(`Failed to record stake: ${result.msg}`);
                    }
                } catch (error) {
                    console.error('Failed to call stake API:', error);
                    alert('Failed to record stake. Please try again.');
                }
            },
            onError: (error) => {
                console.error('Signing error:', error);
                alert('Failed to sign message');
            }
        });
    };

    return (
        <div className="rounded-xl border border-[#2b2b2b] bg-[#0c0c0e] p-6 text-left shadow-[0_20px_70px_rgba(0,0,0,0.5)] sm:p-7">
            <div className="space-y-1">
                <h1 className="text-[24px] font-extrabold leading-[1.5] text-white">Stake Liquidity Privatly</h1>
                <p className="text-[16px] font-medium leading-[1.5] text-[#9fa9b6]">Deposit into ZK-protected pool</p>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                {tokens.map((token) => {
                    const isActive = selectedToken === token.label && !token.disabled;
                    const isDisabled = token.disabled;
                    
                    // Calculate styles based on status
                    let borderColor: string;
                    let backgroundColor: string;
                    let textColor: string;
                    
                    if (isDisabled) {
                        // Already staked: disabled style
                        borderColor = STYLES.disabled.border;
                        backgroundColor = STYLES.disabled.backgroundColor;
                        textColor = STYLES.disabled.text;
                    } else if (isActive) {
                        // Selected: active style
                        borderColor = token.accent;
                        backgroundColor = 'rgba(255,255,255,0.02)';
                        textColor = token.accent;
                    } else {
                        // Default style when not selected or staked
                        borderColor = STYLES.normal.border;
                        backgroundColor = STYLES.normal.backgroundColor;
                        textColor = STYLES.normal.text;
                    }
                    
                    return (
                        <button
                            key={token.label}
                            type="button"
                            onClick={() => !token.disabled && setSelectedToken(token.label)}
                            className={`flex items-center gap-2 rounded-[8px] px-3 py-2 text-[16px] font-medium ${token.disabled ? '' : 'hover:brightness-110'
                                }`}
                            style={{
                                border: `1px solid ${borderColor}`,
                                backgroundColor,
                                color: textColor,
                                cursor: token.disabled ? 'not-allowed' : 'pointer',
                            }}
                            disabled={token.disabled}
                        >
                            <img src={token.icon} alt="" className="h-6 w-6" />
                            <span>{token.label}</span>
                        </button>
                    );
                })}
            </div>

            <div className="mt-8 space-y-2">
                <label className="text-[16px] font-normal leading-[1.5] text-white" htmlFor="token-amount">
                    Token Amount
                </label>
                <div className="rounded-[8px] bg-[#1c2332] px-4 py-3">
                    <p id="token-amount" className="text-center text-[32px] font-extrabold leading-[1.5] text-white">
                        100
                    </p>
                </div>
            </div>

            <button
                type="button"
                onClick={handleStake}
                className={`mt-6 w-full rounded-[8px] px-4 py-3 text-[16px] font-medium leading-[1.5] transition-colors ${selectedToken
                    ? 'bg-[#39e5f5] text-black hover:bg-[#32cddb]'
                    : 'bg-[#4d4d4d] text-[#7b7b7b] cursor-not-allowed'
                    }`}
                disabled={!selectedToken}
            >
                Stake Privatly
            </button>
            <SuccessPopup
                isOpen={showSuccessPopup}
                onClose={() => setShowSuccessPopup(false)}
            />
        </div>
    );
}
